import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { printJson, sanitizeForTerminal, type OutputFormat } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';
import type { EnterpriseCompany } from './types';

interface ViewFlags {
	fields?: string;
}

/**
 * `companies.status`（entity 註解）：0=未上傳註冊文件,1=待審核,2=已通過審核,3=審核未通過。
 * 與 jobs 的 published/unpublished 是不同 domain（審核狀態 vs 刊登旗標），不可共用
 * jobs/list.ts 的 `formatStatus`。目前 CLI/後端皆無既有 company 狀態 formatter，
 * 此處自建、僅供 view 這支命令用。
 */
const COMPANY_STATUS_LABELS: Record<number, string> = {
	0: 'not_submitted',
	1: 'pending_review',
	2: 'approved',
	3: 'rejected',
};

function formatCompanyStatus(status: number | undefined): string {
	if (status === undefined) return '';
	return COMPANY_STATUS_LABELS[status] ?? String(status);
}

/** phone_code + phone_number 合併成一行可讀字串（例：`mobile 0912345678`）。任一缺失則印有值的那個。 */
function formatPhone(company: EnterpriseCompany): string | undefined {
	const code = company.phone_code ?? undefined;
	const number = company.phone_number ?? undefined;
	if (!code && !number) return undefined;
	return [code, number].filter(Boolean).join(' ');
}

/**
 * 資本額顯示：`capital_show_status` 是 client-side-only 顯示旗標（API 一律回傳原始
 * `capital_amount`，是否顯示由前端/CLI 自行決定）。0 → 顯示 "not displayed" 而非金額本身，
 * 避免洩漏公司不想公開的資本額；`capital_amount` 為 null/undefined 則整行省略。
 */
function formatCapital(company: EnterpriseCompany): string | undefined {
	if (company.capital_amount === null || company.capital_amount === undefined) return undefined;
	if (company.capital_show_status === 0) return 'not displayed';
	return String(company.capital_amount);
}

/** area_code 是內部代碼、非人類可讀地名；顯示以 `address`（可讀地址字串）為主。 */
function formatAddress(company: EnterpriseCompany): string | undefined {
	return company.address ?? undefined;
}

const DETAIL_FIELDS: ReadonlyArray<string> = [
	'name',
	'uniform_number',
	'status',
	'website',
	'phone',
	'address',
	'capital',
	'logo_url',
];

function renderDetailLines(company: EnterpriseCompany): string[] {
	const pad = Math.max(...DETAIL_FIELDS.map((f) => f.length)) + 1;
	const derived: Record<string, string | undefined> = {
		name: company.name,
		uniform_number: company.uniform_number ?? undefined,
		status: formatCompanyStatus(company.status),
		website: company.website ?? undefined,
		phone: formatPhone(company),
		address: formatAddress(company),
		capital: formatCapital(company),
		logo_url: company.logo_url ?? undefined,
	};
	const lines: string[] = [];
	for (const field of DETAIL_FIELDS) {
		const value = derived[field];
		if (value === null || value === undefined || value === '') continue;
		lines.push(`${(field + ':').padEnd(pad + 1)}${sanitizeForTerminal(value)}`);
	}
	return lines;
}

/**
 * 五個可整段取代區塊摘要（spec §3.2.1）：table 模式只印筆數，`block_revisions` 是 opaque
 * If-Match token，只在 `--output json` 顯示（讀→改→寫的前提），table 模式一律不印。
 * 固定順序，欄位缺席（GET 契約漂移或本來就沒有）當 0 筆處理，不 throw。
 */
const BLOCK_SUMMARY_FIELDS: ReadonlyArray<string> = ['pain_points', 'awards', 'milestones', 'qa_items', 'video_links'];

function renderBlockSummaryLines(company: EnterpriseCompany): string[] {
	const width = Math.max(...BLOCK_SUMMARY_FIELDS.map((f) => f.length)) + 3;
	return BLOCK_SUMMARY_FIELDS.map((field) => {
		const value = company[field];
		const count = Array.isArray(value) ? value.length : 0;
		return `${(field + ':').padEnd(width)}${count}`;
	});
}

export async function runCompanyView(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat },
	apiKey: string,
	flags: ViewFlags
): Promise<void> {
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/company'
	);
	const company = unwrapDataResponse<EnterpriseCompany>(body);

	if (flags.fields) {
		printJson(pickPaths(company, parseFieldsList(flags.fields)));
		return;
	}
	if (ctx.format === 'json') {
		printJson(company);
		return;
	}
	const lines = [...renderDetailLines(company), '', ...renderBlockSummaryLines(company)];
	process.stdout.write(lines.join('\n') + '\n');
}

/**
 * `company view` 註冊（pm_41 Card B Task 1）。
 */
export function registerEnterpriseCompanyView(parent: Command): void {
	parent
		.command('view')
		.description('View your company information')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.action(async (flags: ViewFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCompanyView(ctx, key, flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { renderDetailLines, formatCompanyStatus, renderBlockSummaryLines };
